import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: '#1D233E',
      paper: '#454567',
    },
    primary: {
      main: '#3987DE',
    },
    text: {
      primary: '#D9D9D9',
      secondary: '#B0B3C6',
    },
    action: {
      disabled: 'rgba(255,255,255,0.38)',
      disabledBackground: 'rgba(255,255,255,0.05)',
    },
  },
  typography: {
    fontFamily: 'Inter, system-ui, sans-serif',
    h1: {
      fontFamily: 'Exo 2, sans-serif',
      fontSize: '48px',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.01em',
      color: '#D9D9D9',
    },
    h2: {
      fontFamily: 'Exo 2, sans-serif',
      fontSize: '36px',
      fontWeight: 600,
      lineHeight: 1.2,
      letterSpacing: '-0.005em',
      color: '#D9D9D9',
    },
    h3: {
      fontFamily: 'Exo 2, sans-serif',
      fontSize: '28px',
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '-0.0025em',
      color: '#D9D9D9',
    },
    h4: {
      fontFamily: 'Exo 2, sans-serif',
      fontSize: '32px',
      fontWeight: 700,
      lineHeight: 1.2,
      color: '#D9D9D9',
    },
    body1: {
      fontFamily: 'Inter, system-ui, sans-serif',
      fontSize: '15px',
      fontWeight: 400,
      lineHeight: 1.5,
      color: '#D9D9D9',
    },
    body2: {
      fontFamily: 'Inter, system-ui, sans-serif',
      fontSize: '16px',
      fontWeight: 400,
      lineHeight: 1.5,
      color: '#D9D9D9',
    },
    subtitle2: {
      fontFamily: 'Inter, system-ui, sans-serif',
      fontSize: '14px',
      fontWeight: 400,
      lineHeight: 1.5,
      color: '#B0B3C6',
    },
    button: {
      fontFamily: 'Inter, system-ui, sans-serif',
      fontSize: '12px',
      fontWeight: 500,
      lineHeight: 1.5,
      letterSpacing: '0.02em',
      color: '#D9D9D9',
      textTransform: 'none',
    },
    // You can add more variants as needed
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 8px 32px rgba(0,0,0,0.24)',
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'filled',
      },
      styleOverrides: {
        root: {
          '& .MuiInputLabel-root': {
            // Ensure floating labels have adequate space
            '&.MuiInputLabel-shrink': {
              transform: 'translate(12px, 7px) scale(0.75)',
              transformOrigin: 'top left',
            },
          },
          '& .MuiFilledInput-root': {
            // Prevent label overlap by ensuring minimum height
            minHeight: '56px',
            '&.Mui-focused, &:hover': {
              '& .MuiInputLabel-root': {
                transform: 'translate(12px, 7px) scale(0.75)',
                color: 'primary.main',
              },
            },
          },
          '& .MuiOutlinedInput-root': {
            // Ensure outlined variant has proper spacing
            '& .MuiInputLabel-root': {
              '&.MuiInputLabel-shrink': {
                transform: 'translate(14px, -9px) scale(0.75)',
                backgroundColor: 'background.default',
                padding: '0 8px',
                borderRadius: '4px',
              },
            },
          },
          // Utility class for multiline TextFields with specific row counts
          '&.MuiTextField-multilineRows': {
            '& .MuiInputBase-input': {
              minHeight: '56px', // Base height for proper floating labels
            },
          },
        },
        MuiFilledInput: {
          styleOverrides: {
            root: {
              backgroundColor: 'rgba(255,255,255,0.10)',
              borderRadius: 8,
              height: '42px',
              '&:before, &:after, &:hover:before, &.Mui-focused:before, &.Mui-focused:after, &.Mui-error:after':
                {
                  borderBottom: '0 !important',
                },
            },
            input: {
              padding: '26px 14px 8px',
              fontSize: '16px',
            },
          },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            root: {
              color: '#D9D9D9',
              fontWeight: 500,
              '&.Mui-disabled': {
                backgroundColor: 'rgba(255,255,255,0.05) !important',
                color: 'rgba(255,255,255,0.38) !important',
              },
            },
            input: {
              color: '#D9D9D9',
              '&.Mui-disabled': {
                color: 'rgba(255,255,255,0.38) !important',
                WebkitTextFillColor: 'rgba(255,255,255,0.38) !important',
              },
            },
            notchedOutline: {
              borderColor: 'rgba(255,255,255,0.3)',
              '&.Mui-disabled': {
                borderColor: 'rgba(255,255,255,0.12) !important',
              },
            },
          },
        },
        MuiInputLabel: {
          styleOverrides: {
            root: {
              color: '#D9D9D9',
              '&.Mui-focused': {
                color: '#D9D9D9',
              },
              '&.Mui-disabled': {
                color: 'rgba(255,255,255,0.38) !important',
              },
            },
            shrink: {
              // Ensure consistent padding when labels float
              paddingRight: '8px !important',
              paddingLeft: '8px !important',
              backgroundColor: 'background.default !important',
            },
          },
        },
        MuiDivider: {
          styleOverrides: {
            root: {
              color: '#D9D9D9',
              fontWeight: 500,
            },
          },
        },
        MuiButton: {
          styleOverrides: {
            root: {
              borderRadius: 8,
              minHeight: 40,
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: {
              backgroundImage: 'none',
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              backgroundColor: 'background.paper',
              '&.MuiCard-root': {
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              },
            },
          },
        },
        MuiSwitch: {
          styleOverrides: {
            track: {
              backgroundColor: '#44475a',
              opacity: 1,
            },
            thumb: {
              backgroundColor: '#fff',
            },
            switchBase: {
              '&.Mui-checked + .MuiSwitch-track': {
                backgroundColor: '#3987DE',
                opacity: 1,
              },
            },
          },
        },
        MuiAutocomplete: {
          styleOverrides: {
            root: {
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#262638',
                borderRadius: 8,
                color: '#D9D9D9',
                fontWeight: 500,
                '@media (max-width: 600px)': {
                  minHeight: '48px', // Larger touch target on mobile
                },
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255,255,255,0.3)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255,255,255,0.5)',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#3987DE',
              },
              '& .MuiInputLabel-root': {
                color: '#D9D9D9',
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#D9D9D9',
              },
              '& .MuiAutocomplete-clearIndicator': {
                color: 'rgba(255,255,255,0.8)',
              },
              '& .MuiAutocomplete-popupIndicator': {
                color: 'rgba(255,255,255,0.8)',
              },
              // Mobile optimizations
              '& .MuiAutocomplete-tag': {
                '@media (max-width: 600px)': {
                  fontSize: '12px',
                  padding: '4px 8px',
                  margin: '2px',
                },
              },
              // Disabled state - comprehensive styling
              '&.Mui-disabled': {
                cursor: 'not-allowed !important',
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(255,255,255,0.05) !important',
                  color: 'rgba(255,255,255,0.38) !important',
                  cursor: 'not-allowed !important',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255,255,255,0.12) !important',
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255,255,255,0.38) !important',
                },
                '& .MuiAutocomplete-clearIndicator': {
                  color: 'rgba(255,255,255,0.38) !important',
                },
                '& .MuiAutocomplete-popupIndicator': {
                  color: 'rgba(255,255,255,0.38) !important',
                },
                '& .MuiOutlinedInput-input': {
                  color: 'rgba(255,255,255,0.38) !important',
                  WebkitTextFillColor: 'rgba(255,255,255,0.38) !important',
                  cursor: 'not-allowed !important',
                },
              },
            },
            tag: {
              backgroundColor: '#3987DE',
              color: '#fff',
              fontWeight: 500,
              borderRadius: '20px',
              padding: '6px 12px',
              margin: '4px',
              fontSize: '13px',
              border: 'none',
              boxShadow: '0 2px 4px rgba(57, 135, 222, 0.2)',
              '&:hover': {
                backgroundColor: '#2d6bb8',
                boxShadow: '0 3px 6px rgba(57, 135, 222, 0.3)',
              },
              '& .MuiChip-deleteIcon': {
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '16px',
                marginLeft: '4px',
                '&:hover': {
                  color: '#fff',
                },
              },
              '@media (max-width: 600px)': {
                fontSize: '12px',
                padding: '4px 10px',
                margin: '2px',
                borderRadius: '16px',
              },
            },
          },
        },
      },
      orbit: {
        surface: '#454567',
        highlightPrimary: '#3987DE',
      },
    } as any,
  },
});

export default theme;
